import React, { useState, useEffect, useCallback, useRef } from 'react';
import TimePicker from '../components/TimePicker';
import GoalGuardrailModal from '../components/GoalGuardrailModal';
import { validateKmDaysMatrix } from '../utils/goalValidator';
import { runningMissingFields, runningMissingMessage, runningMatrixCheck, runningGoalGuardrail, runningPlanCheck,
         runningDaysCount, saveErrorMessage, trainingSetPayload, fetchRunningAvailability,
         allowedRangeText } from '../utils/runningSaveRules';
import useSaveDialog from '../components/profile/useSaveDialog';
import { isoToParts, evaluateDobParts } from '../utils/age';
import DateOfBirthInput from '../components/profile/DateOfBirthInput';
import LoadBalanceModal from '../components/LoadBalanceModal';
import authFetch from '../utils/authFetch';
import { planRegenRequest } from '../utils/planRegenRequest';
import { CYCLING_ENABLED } from '../config/features';
import NotificationOptInStep from '../components/NotificationOptInStep';
import GarminLogo from '../components/brand/GarminLogo';
import { LevelMeter } from '../components/profile/ProfileParts';
import { PersonSimpleRun, Bicycle, ArrowsClockwise, CheckCircle } from '@phosphor-icons/react';

// Map the goalValidator's canonical labels ('5K'/'10K'/'חצי מרתון'/'מרתון') back to the internal distance values
// ('5'/'10'/'21.1'/'42.2') — the guardrail's suggested distance.
const distanceLabelToValue = (lbl) => ({
  '5K': '5',
  '10K': '10',
  'חצי מרתון': '21.1',
  'מרתון': '42.2',
}[lbl] || null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Shared helpers ──────────────────────────────────────────────────
const DAYS = [
  { key: 'ראשון', label: 'א' },
  { key: 'שני',   label: 'ב' },
  { key: 'שלישי', label: 'ג' },
  { key: 'רביעי', label: 'ד' },
  { key: 'חמישי', label: 'ה' },
  { key: 'שישי',  label: 'ו' },
  { key: 'שבת',   label: 'ש' },
];

const DISTANCES = [
  { label: '5K',   value: '5',    km: 5    },
  { label: '10K',  value: '10',   km: 10   },
  { label: 'חצי',  value: '21.1', km: 21.1 },
  { label: 'מרתון',value: '42.2', km: 42.2 },
];

// ── Saving a step (owner 2026-11-02) ────────────────────────────────
// A step moves on ONLY when the server stored it. Before this, every step but the first ignored the response: a
// rejected save (e.g. 400 marathon_unavailable), a network error or a hang still went to the next step, the wizard
// showed the values from its own memory, and the athlete finished onboarding with no goal in the profile — the first
// plan then failed with 400 profile_incomplete (Play test account, 2026-11-02).
export const SAVE_TIMEOUT_MS = 20000;
export const SAVE_NETWORK_MSG = 'אין חיבור לשרת — הנתונים לא נשמרו. בדוק את החיבור ונסה שוב.';
export const SAVE_TIMEOUT_MSG = 'השרת לא הגיב בזמן — הנתונים לא נשמרו. נסה שוב.';

/** PUT `body` to `url` and report whether the server stored it: { ok, status, message }. Never throws. */
export async function saveProfileRequest(fetcher, url, body, { timeoutMs = SAVE_TIMEOUT_MS, method = 'PUT' } = {}) {
  let timer = null;
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve({ timedOut: true }), timeoutMs); });
  try {
    const res = await Promise.race([
      fetcher(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
      timeout,
    ]);
    if (res && res.timedOut) return { ok: false, status: 0, message: SAVE_TIMEOUT_MSG };
    if (res && res.ok) return { ok: true, status: res.status, message: null };
    let errBody = null;
    try { errBody = await res.json(); } catch { /* not JSON */ }
    // The Profile screen's own extraction (runningSaveRules.saveErrorMessage) — the same text on both screens.
    return { ok: false, status: res ? res.status : 0,
             message: saveErrorMessage(errBody,`שגיאה בשמירה (${res ? res.status : '?'}) — הנתונים לא נשמרו. נסה שוב.`) };
  } catch {
    return { ok: false, status: 0, message: SAVE_NETWORK_MSG };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

const GOAL_TIME_RE = /^\d{1,2}:\d{2}(:\d{2})?$/;
/** The running goal the profile must hold before a first plan: a distance and a goal time (a goal RACE is optional). */
export function runningGoalSaved(profile) {
  const dist = String((profile && profile.goal_distance) || '').trim();
  const time = String((profile && profile.goal_time) || '').trim();
  return !!dist && GOAL_TIME_RE.test(time);
}
export const GOAL_MISSING_MSG = 'מרחק היעד וזמן היעד לא נשמרו בפרופיל. חזור לצעד היעד, בחר אותם ושמור שוב.';

// ── Main component ──────────────────────────────────────────────────
// Step order per selected sports. 9 = notification opt-in, always right before 6 (welcome + plan generation,
// unchanged). Module-level + exported so the three onboarding paths are unit-tested.
export function onboardingStepSequence(sports) {
  const r = sports.includes('running');
  const c = sports.includes('cycling');
  if (r && c) return [1, 2, 3, 7, 4, 8, 5, 9, 6];
  if (r)      return [1, 2, 3, 4, 5, 9, 6];
  if (c)      return [1, 2, 7, 8, 9, 6];
  return [1, 2, 9, 6];
}

function OnboardingWizard({ userId, userEmail, onComplete, isJoiningCoach = false, onViewPlan }) {
  // Resilient to remounts: a Firebase auth re-fire (token refresh during the long
  // plan-generation step) briefly flips checkingUserType/checkingOnboarding in App.js,
  // which unmounts the wizard. Without persistence, currentStep reset to 1 and threw
  // the user back to the name screen at completion. Resume the saved step instead.
  const [currentStep, setCurrentStep] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`onb_step_${userId}`);
      if (saved) return Number(saved) || 1;
    } catch {}
    return 1;
  });
  useEffect(() => {
    try { sessionStorage.setItem(`onb_step_${userId}`, String(currentStep)); } catch {}
  }, [currentStep, userId]);
  const [isLoading, setIsLoading] = useState(false);

  // Step sequence depends on chosen sports: 7=cycling profile, 8=cycling race goal
  const getStepSequence = onboardingStepSequence;

  // Accumulated form data across all steps
  const [form, setForm] = useState({
    // Step 1
    name: '',
    age: '',
    date_of_birth: '',
    // Step 2
    sports: [], // ['running'], ['cycling'], ['running','cycling']
    // Step 3
    level: '',
    avg_weekly_km: '',
    preferred_days: '',
    preferred_long_run_day: '',
    // Step 4
    has_race: null, // null = not chosen, true/false
    goal_distance: '',
    goal_date: '',
    goal_race: '',
    goal_time: '',
    // Step 5 (optional)
    pr_5k: '',
    pr_10k: '',
    pr_half: '',
    pr_full: '',
    max_hr: '',
    injury_prone_areas: '',
    strength_training: false,
    strength_days_per_week: '',
    // Step 7 — cycling profile
    cycling_level: '',
    bike_types: [],
    avg_weekly_cycling_km: '',
    preferred_cycling_days: '',
    has_power_meter: false,
    has_smart_trainer: false,
    ftp: '',
    // Step 8 — cycling race goal
    has_cycling_race: null,
    cycling_goal_distance: '',
    cycling_goal_date: '',
    cycling_race_name: '',
    cycling_goal_time: '',
    cycling_preferred_distance: '',
  });

  const [pickerOpen, setPickerOpen] = useState(null);
  // One save at a time across the steps: a double tap before the re-render never sends a second request (the steps
  // are inline components — a ref at THIS level survives their remounts).
  const saveInFlightRef = useRef(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const isRunning = form.sports.includes('running');

  const nextStep = () => {
    const seq = getStepSequence(form.sports);
    setCurrentStep(s => {
      const idx = seq.indexOf(s);
      return idx < seq.length - 1 ? seq[idx + 1] : s;
    });
  };
  const prevStep = () => {
    const seq = getStepSequence(form.sports);
    setCurrentStep(s => {
      const idx = seq.indexOf(s);
      return idx > 0 ? seq[idx - 1] : s;
    });
  };

  // Pre-fill from existing profile
  useEffect(() => {
    const load = async () => {
      try {
        const r = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`);
        if (r.ok) {
          const p = await r.json();
          setForm(f => ({
            ...f,
            name: p.name || '',
            age: p.age ? String(p.age) : '',
            date_of_birth: p.date_of_birth ? String(p.date_of_birth).slice(0, 10) : '',
            level: p.level || '',
            avg_weekly_km: p.avg_weekly_km ? String(p.avg_weekly_km) : '',
            preferred_days: p.preferred_days || '',
            preferred_long_run_day: p.preferred_long_run_day || '',
            goal_distance: p.goal_distance || '',
            goal_date: p.goal_date || '',
            goal_race: p.goal_race || '',
            goal_time: p.goal_time || '',
            max_hr: p.max_hr ? String(p.max_hr) : '',
            injury_prone_areas: p.injury_prone_areas || '',
            strength_training: p.strength_training || false,
            strength_days_per_week: p.strength_days_per_week ? String(p.strength_days_per_week) : '',
          }));
        }
        const cr = await authFetch(`${API_BASE_URL}/profile/cycling/${userId}`);
        if (cr.ok) {
          const cp = await cr.json();
          if (cp?.sports_active) {
            setForm(f => ({
              ...f,
              sports: cp.sports_active.split(','),
              cycling_level: cp.cycling_level || '',
              bike_types: cp.bike_types ? cp.bike_types.split(',') : [],
              avg_weekly_cycling_km: cp.avg_weekly_cycling_km ? String(cp.avg_weekly_cycling_km) : '',
              preferred_cycling_days: cp.preferred_cycling_days || '',
              has_power_meter: cp.has_power_meter || false,
              has_smart_trainer: cp.has_smart_trainer || false,
              ftp: cp.ftp ? String(cp.ftp) : '',
              cycling_goal_distance: cp.cycling_goal_distance || '',
              cycling_goal_date: cp.cycling_goal_date || '',
              cycling_race_name: cp.cycling_goal_event || '',
              cycling_goal_time: cp.cycling_goal_time || '',
            }));
          }
        }
      } catch {}
    };
    load();
  }, [userId]);

  // ── Progress bar ────────────────────────────────────────────────
  const ProgressBar = () => {
    const seq = getStepSequence(form.sports);
    const currentIdx = seq.indexOf(currentStep);
    return (
      <div className="flex items-center gap-1.5 mb-6">
        {seq.map((_, i) => (
          <div
            key={i}
            className="h-1.5 rounded-full flex-1 transition-all duration-300"
            style={{ background: i <= currentIdx ? 'var(--g-accent)' : 'var(--g-bar)' }}
          />
        ))}
      </div>
    );
  };

  // ── Back/Next buttons ────────────────────────────────────────────
  const NavButtons = ({ onNext, nextLabel = 'המשך ←', nextDisabled = false, showBack = true, nextLoading = false }) => (
    <div className="flex gap-3 mt-6">
      {showBack && currentStep > 1 && (
        <button
          type="button"
          onClick={prevStep}
          className="flex-1 h-[52px] rounded-full border border-border text-text text-[16px] font-semibold"
        >
          → חזור
        </button>
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled || nextLoading}
        className="flex-1 h-[52px] rounded-full text-[16px] btn-primary transition-colors"
        style={{ flexGrow: 2 }}
      >
        {nextLoading ? 'שומר...' : nextLabel}
      </button>
    </div>
  );

  // Partial date of birth typed in Step 1 (2026-10-20). The steps are defined inline, so a wizard re-render
  // REMOUNTS Step 1 and resets its local state; the day / month / year typed so far are kept here, at wizard
  // level, in a ref (no re-render on each keystroke, so the field never loses focus) and restored on remount.
  const dobPartsRef = useRef(null);

  // ──────────────────────────────────────────────────────────────────
  // STEP 1 — שם + תאריך לידה
  // ──────────────────────────────────────────────────────────────────
  const Step1 = () => {
    const [localName, setLocalName] = useState(form.name);
    // Date of birth in three fields (DateOfBirthInput); the age is computed (Israel date) and shown by the input.
    const [dobParts, setDobPartsState] = useState(() => dobPartsRef.current || isoToParts(form.date_of_birth));
    const setDobParts = (next) => { dobPartsRef.current = next; setDobPartsState(next); };
    const [dobShowErrors, setDobShowErrors] = useState(false);
    const [saving, setSaving] = useState(false);
    const dobEval = evaluateDobParts(dobParts);

    const handleNext = async () => {
      if (!localName.trim()) { alert('נא להזין שם'); return; }
      if (dobEval.status !== 'valid') { setDobShowErrors(true); return; }   // required; the input shows why
      const localDob = dobEval.iso;
      const localAge = dobEval.age;
      setSaving(true);
      try {
        const resp = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: localName.trim(), date_of_birth: localDob, email: userEmail })
        });
        // The old code only caught network/CORS errors — a 500 from the
        // backend was silently swallowed and the wizard advanced to step
        // 2 even though no profile was saved. That left the user with a
        // 404 on /profile in every later step and no plan generation in
        // chat. Now we surface backend errors as a real failure.
        if (!resp.ok) {
          let detail = '';
          try { detail = (await resp.text()).slice(0, 200); } catch {}
          console.error(`PUT /profile failed: ${resp.status} ${resp.statusText}`, detail);
          alert(`שגיאה בשמירה (${resp.status}). נסה שוב — אם זה ממשיך, צור קשר`);
          return;
        }
        set('name', localName.trim());
        set('date_of_birth', localDob);
        set('age', String(localAge));                 // computed — feeds the max-HR placeholder as before
        nextStep();
      } catch (err) {
        // Real network/CORS failure — log so we can diagnose from devtools.
        console.error('PUT /profile threw:', err);
        alert('שגיאה בשמירה — בדוק חיבור לאינטרנט ונסה שוב');
      }
      finally { setSaving(false); }
    };

    return (
      <div>
        <h2 className="text-2xl font-bold text-text mb-1">פרטים אישיים</h2>
        <p className="text-text-muted text-sm mb-6">בסיס לתכנית המותאמת שלך</p>
        <div className="space-y-4">
          <div>
            <label className="block text-text-body text-sm mb-1">שם מלא *</label>
            <input
              type="text"
              value={localName}
              onChange={e => setLocalName(e.target.value)}
              placeholder="לדוגמה: ליאור כהן"
              className="w-full p-3 rounded-xl bg-pressed border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
            />
          </div>
          <div data-testid="ob-dob">
            <DateOfBirthInput idPrefix="ob-dob" parts={dobParts} onPartsChange={setDobParts}
                              showErrors={dobShowErrors} required />
          </div>
        </div>
        <NavButtons
          onNext={handleNext}
          nextDisabled={!localName.trim() || dobEval.status === 'empty'}
          nextLoading={saving}
          showBack={false}
        />
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // STEP 2 — ענף ספורט
  // ──────────────────────────────────────────────────────────────────
  const Step2 = () => {
    // Don't pre-select from DB — user must explicitly choose their sport in onboarding.
    // With cycling hidden (CYCLING_ENABLED=false) running is the only option → pre-select it so the
    // step is a single confirming tap (Next is enabled immediately) instead of a pointless 1-of-1 pick.
    const [sel, setSel] = useState(CYCLING_ENABLED ? [] : ['running']);
    const [saving, setSaving] = useState(false);
    const dlg = useSaveDialog();

    const toggle = (sport) => {
      setSel(prev => prev.includes(sport) ? prev.filter(s => s !== sport) : [...prev, sport]);
    };

    const handleNext = async () => {
      if (!sel.length) { alert('בחר לפחות ענף אחד'); return; }
      if (saveInFlightRef.current) return;
      saveInFlightRef.current = true;
      setSaving(true);
      try {
        const r = await saveProfileRequest(authFetch, `${API_BASE_URL}/profile/cycling?user_id=${userId}`,
                                           { sports_active: sel.join(',') });
        if (!r.ok) { dlg.showBlock(r.message, 'השמירה נכשלה'); return; }   // stay here — nothing was stored
        set('sports', sel);
        // Navigate based on local `sel` — form.sports is async
        const seq = getStepSequence(sel);
        setCurrentStep(seq[seq.indexOf(2) + 1]);
      } finally { saveInFlightRef.current = false; setSaving(false); }
    };

    const Btn = ({ sport, icon, label }) => (
      <button
        type="button"
        onClick={() => toggle(sport)}
        className={`flex-1 py-5 rounded-2xl font-bold text-lg transition-all border-2 flex flex-col items-center gap-2 ${
          sel.includes(sport)
            ? 'bg-pressed border-accent text-text'
            : 'bg-pressed border-border text-text-muted hover:border-border'
        }`}
      >
        <span className="text-[34px] flex">{icon}</span>
        <span>{label}</span>
      </button>
    );

    return (
      <div>
        <h2 className="text-2xl font-bold text-text mb-1">ענף ספורט</h2>
        {CYCLING_ENABLED && <p className="text-text-muted text-sm mb-6">ניתן לבחור יותר מאחד</p>}
        <div className="flex gap-3">
          <Btn sport="running" icon={<PersonSimpleRun weight="bold" />} label="ריצה" />
          {CYCLING_ENABLED && <Btn sport="cycling" icon={<Bicycle weight="bold" />} label="רכיבה" />}
        </div>
        <NavButtons onNext={handleNext} nextDisabled={!sel.length} nextLoading={saving} />
        {dlg.element}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // STEP 3 — רמה + ק"מ שבועי + ימי אימון
  // ──────────────────────────────────────────────────────────────────
  const Step3 = () => {
    const [level, setLevel] = useState(form.level);
    const [km, setKm] = useState(form.avg_weekly_km);
    const [days, setDays] = useState(
      form.preferred_days ? form.preferred_days.split(' ').filter(Boolean) : []
    );
    const [longDay, setLongDay] = useState(form.preferred_long_run_day);
    const [saving, setSaving] = useState(false);
    // Same hard block as the Profile screen: refuse to save an
    // unbalanced km × days combo. User report 2026-06-07: new athlete
    // signed up with 50 km/week + 2 days + half marathon goal and was
    // never warned. The same physiological floor must gate onboarding.
    const [loadBalanceModal, setLoadBalanceModal] = useState({ open: false });
    const dlg = useSaveDialog();

    const toggleDay = (d) => {
      const order = DAYS.map(x => x.key);
      if (days.includes(d) && longDay === d) setLongDay('');   // the long-run day must stay one of the run days
      setDays(prev => {
        const next = prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d];
        return next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
      });
    };

    const KM_OPTIONS = ['20', '25', '30', '35', '40', '50', '60'];

    const handleNext = async () => {
      if (!level) { alert('בחר רמה'); return; }
      if (!km || parseFloat(km) <= 0) { alert('הזן ק"מ שבועי'); return; }
      if (!days.length) { alert('בחר לפחות יום אימון אחד'); return; }
      // Hard block: km × days. Mirrors the Profile-screen guard. For a 10K
      // goal this routes through the distance-scoped runner-level band +
      // profile envelope (validateProfile10K); other distances keep the
      // legacy matrix. goal_distance may be unset at this step → legacy path.
      const _matrixCheck = validateKmDaysMatrix(parseFloat(km), days.length,
        { distance: form.goal_distance, level });
      if (!_matrixCheck.ok) {
        if (_matrixCheck.errorCode) {
          dlg.showBlock(`${_matrixCheck.reason}`);
          return;
        }
        setLoadBalanceModal({
          open: true,
          weeklyKm: parseFloat(km),
          trainingDays: days.length,
          requiredDays: _matrixCheck.requiredDays,
        });
        return;
      }
      if (saveInFlightRef.current) return;
      saveInFlightRef.current = true;
      setSaving(true);
      try {
        const prefDays = days.join(' ');
        const r = await saveProfileRequest(authFetch, `${API_BASE_URL}/profile?user_id=${userId}`, {
          level,
          avg_weekly_km: parseFloat(km),
          preferred_days: prefDays,
          training_days_per_week: days.length,
          preferred_long_run_day: longDay || null,
        });
        if (!r.ok) { dlg.showBlock(r.message, 'השמירה נכשלה'); return; }   // stay here — nothing was stored
        set('level', level);
        set('avg_weekly_km', km);
        set('preferred_days', prefDays);
        set('preferred_long_run_day', longDay);
        nextStep();
      } finally { saveInFlightRef.current = false; setSaving(false); }
    };

    // Runner levels — approved terminology (Phase 3): מתחיל / מנוסה / מתקדם,
    // with the 10K runner-level mileage bands (BEGINNER 0–25, EXPERIENCED
    // 26–40, ADVANCED 41+). Legacy value 'intermediate' reads as 'experienced'
    // (מנוסה) — never shown as 'בינוני' for the active running product.
    const LEVELS = [
      { key: 'beginner',    label: 'מתחיל', desc: 'עד 25 ק"מ' },
      { key: 'experienced', label: 'מנוסה', desc: '26–40 ק"מ' },
      { key: 'advanced',    label: 'מתקדם', desc: '41+ ק"מ' },
    ];
    // Also accept Hebrew + legacy keys. 'experienced' absorbs the legacy
    // 'intermediate'/'בינוני' rows so an existing profile still highlights.
    const isLevel = (k) => level === k ||
      (k === 'beginner' && level === 'מתחיל') ||
      (k === 'experienced' && (level === 'מנוסה' || level === 'experienced' ||
        level === 'בינוני' || level === 'intermediate')) ||
      (k === 'advanced' && (level === 'מתקדם' || level === 'advanced'));

    // Graphite step 3 (2026-10-27): readable type scale + 48px targets. Unselected chip = transparent, line border,
    // sub text; selected = lime fill, #111 text. Long-run day = lime outline, offered only on a chosen run day.
    const LABEL3 = 'block text-[15px] font-bold text-text mb-3';
    const HELP3 = 'text-[13px] leading-[1.5] text-text-muted';
    const CHIP = 'h-12 rounded-[12px] border transition-colors';
    const chipCls = (on) => `${CHIP} ${on ? 'bg-accent border-accent text-on-accent' : 'bg-transparent border-border text-text-body'}`;
    const req = <span className="text-text-muted font-normal"> *</span>;
    const customKm = !KM_OPTIONS.includes(km) && !!km;

    return (
      <div>
        <h2 className="text-[26px] font-extrabold leading-tight text-text mb-1">פרופיל ריצה</h2>
        <p className="text-[15px] text-text-muted mb-6">הבסיס לחישוב התכנית המותאמת</p>

        {/* Level */}
        <div className="mb-6">
          <span className={LABEL3}>רמה{req}</span>
          <div className="grid grid-cols-3 gap-2">
            {LEVELS.map(l => (
              <button
                key={l.key}
                type="button"
                aria-pressed={isLevel(l.key)}
                onClick={() => setLevel(l.key)}
                className={`min-h-[84px] py-3 px-1 rounded-[12px] bg-transparent transition-colors ${
                  isLevel(l.key) ? 'text-text' : 'text-text-body'
                }`}
                style={{ border: isLevel(l.key) ? '1.5px solid var(--g-accent)' : '1px solid var(--g-line)' }}
              >
                <div className="flex justify-center mb-2"><LevelMeter level={l.key} /></div>
                <div className="text-[16px] font-bold">{l.label}</div>
                <div className="text-[13px] text-text-body mt-0.5">{l.desc}</div>
              </button>
            ))}
          </div>
          <p className={`${HELP3} mt-2 text-center`}>לפי ק"מ שבועי שאתה רץ בדרך כלל</p>
        </div>

        {/* Weekly KM — 2 rows × 4: [20 25 30 35] / [40 50 60 אחר] */}
        <div className="mb-6">
          <label htmlFor="onb-km-other" className={LABEL3}>ק"מ שבועי ממוצע{req}</label>
          <div className="grid grid-cols-4 gap-2">
            {KM_OPTIONS.map(k => (
              <button key={k} type="button" aria-pressed={km === k} onClick={() => setKm(k)}
                      className={`${chipCls(km === k)} num-display text-[18px]`} style={{ fontWeight: 700 }}>
                {k}
              </button>
            ))}
            <input
              id="onb-km-other"
              type="number"
              inputMode="numeric"
              value={KM_OPTIONS.includes(km) ? '' : km}
              onChange={e => setKm(e.target.value)}
              placeholder="אחר"
              aria-label='ק"מ שבועי — ערך אחר'
              className={`${chipCls(customKm)} w-full min-w-0 px-2 text-center text-[16px] font-semibold focus:outline-none focus:border-accent ${
                customKm ? 'placeholder-on-accent' : 'placeholder-text-body'}`}
            />
          </div>
        </div>

        {/* Preferred days */}
        <div className="mb-6">
          <span className={LABEL3}>ימי אימון מועדפים{req}</span>
          <div className="grid grid-cols-7 gap-2">
            {DAYS.map(d => (
              <button key={d.key} type="button" aria-pressed={days.includes(d.key)} aria-label={`יום ${d.key}`}
                      onClick={() => toggleDay(d.key)}
                      className={`${chipCls(days.includes(d.key))} text-[16px] font-semibold`}>
                {d.label}
              </button>
            ))}
          </div>
          {days.length > 0 && (
            <p className={`${HELP3} mt-2 text-center`}>{days.length} ימי אימון בשבוע</p>
          )}
        </div>

        {/* יום ריצה ארוכה — רק מבין ימי האימון שנבחרו */}
        <div className="mb-6">
          <span className={LABEL3}>יום ריצה ארוכה מועדף</span>
          <div className="grid grid-cols-7 gap-2">
            {DAYS.map(d => {
              const isRunDay = days.includes(d.key);
              const on = longDay === d.key;
              return (
                <button key={d.key} type="button" disabled={!isRunDay} aria-pressed={on} aria-label={`ריצה ארוכה ביום ${d.key}`}
                  onClick={() => setLongDay(on ? '' : d.key)}
                  className={`${CHIP} text-[16px] font-semibold disabled:cursor-not-allowed ${
                    on ? 'bg-transparent text-accent' : 'bg-transparent border-border text-text-body'}`}
                  style={{ ...(on ? { border: '1.5px solid var(--g-accent)' } : {}), ...(isRunDay ? {} : { opacity: 0.35 }) }}
                >{d.label}</button>
              );
            })}
          </div>
          <p className={`${HELP3} mt-2`}>לא חובה — אם לא תבחר, נשים ביום האחרון שלך</p>
        </div>

        <NavButtons onNext={handleNext} nextDisabled={!level || !km || !days.length} nextLoading={saving} />
        <LoadBalanceModal
          open={loadBalanceModal.open}
          weeklyKm={loadBalanceModal.weeklyKm}
          trainingDays={loadBalanceModal.trainingDays}
          requiredDays={loadBalanceModal.requiredDays}
          onFix={() => setLoadBalanceModal({ open: false })}
        />
        {dlg.element}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // STEP 4 — מרוץ יעד
  // ──────────────────────────────────────────────────────────────────
  const Step4 = () => {
    const [hasRace, setHasRace] = useState(form.has_race === true);
    const [dist, setDist] = useState(form.goal_distance);
    const [date, setDate] = useState(form.goal_date);
    const [raceName, setRaceName] = useState(form.goal_race);
    // map: זמן יעד נפרד לכל מרחק
    const [timesMap, setTimesMap] = useState(() => ({
      '5':    form.goal_distance === '5'    ? form.goal_time : '',
      '10':   form.goal_distance === '10'   ? form.goal_time : '',
      '21.1': form.goal_distance === '21.1' ? form.goal_time : '',
      '42.2': form.goal_distance === '42.2' ? form.goal_time : '',
    }));
    const [localPicker, setLocalPicker] = useState(false);
    const [saving, setSaving] = useState(false);
    const [guardrail, setGuardrail] = useState(null); // {result} | null
    const [loadBalance, setLoadBalance] = useState({ open: false });
    const dlg = useSaveDialog();
    // The plan engine's answer for this distance on the training set from Step 3 (GET /policy/running-availability,
    // the same as the Profile screen): the allowed weekly-km range, or why this combination cannot get a plan — shown
    // before "המשך". Display only; handleNext asks again and blocks.
    const [planHint, setPlanHint] = useState(null);
    useEffect(() => {
      let live = true;
      setPlanHint(null);
      if (dist) {
        fetchRunningAvailability(authFetch, API_BASE_URL, { ...form, goal_distance: dist })
          .then((av) => { if (live) setPlanHint(av); });
      }
      return () => { live = false; };
    }, [dist, form.avg_weekly_km, form.preferred_days, form.level, form.preferred_long_run_day]);   // eslint-disable-line react-hooks/exhaustive-deps
    const daysChosen = runningDaysCount(form.preferred_days);

    const goalTime = timesMap[dist] || '';
    const setGoalTime = (v) => setTimesMap(m => ({ ...m, [dist]: v }));
    const distKm = DISTANCES.find(d => d.value === dist)?.km || null;

    // The goal is saved TOGETHER with the training set it was chosen for (level, weekly km, days, long-run day — the
    // values Step 3 stored), exactly like the Profile screen's save: PUT /profile then checks the whole combination
    // (marathon availability, km x days, the weekly-km floor by goal and level — whatever the plan engine cannot build)
    // and its answer is final. Only fields with a value go out, so nothing else in the profile is touched or cleared.
    // Never an override (owner 2026-11-02): `goalWarningAccepted` only marks the goal race as "the athlete saw the
    // goal-time warning", it never skips a rule.
    const persistGoal = async (goalWarningAccepted) => {
      if (saveInFlightRef.current) return;
      saveInFlightRef.current = true;
      setSaving(true);
      try {
        const r = await saveProfileRequest(authFetch, `${API_BASE_URL}/profile?user_id=${userId}`, {
          ...trainingSetPayload(form),
          goal_distance: dist,
          goal_date: hasRace ? date : null,
          goal_race: hasRace ? raceName.trim() : null,
          goal_time: goalTime,
          goal_override_acknowledged: false,
        });
        // stay here with the choice — nothing was stored; the server's reason, as the Profile screen shows it
        if (!r.ok) { dlg.showBlock(r.message, 'השמירה נכשלה'); return; }

        // שמור גם לטבלת races אם יש תחרות
        if (hasRace && raceName.trim() && date) {
          const catMap = { '5': '5k', '10': '10k', '21.1': 'half', '42.2': 'marathon' };
          await authFetch(`${API_BASE_URL}/races?user_id=${userId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              race_name: raceName.trim(),
              race_date: date,
              distance_category: catMap[dist] || 'other',
              goal_time: goalTime,
              is_goal_race: true,
              override_goal: !!goalWarningAccepted,
            })
          }).catch(() => {});
        }

        set('has_race', hasRace);
        set('goal_distance', dist);
        set('goal_date', date);
        set('goal_race', raceName);
        set('goal_time', goalTime);
        nextStep();
      } finally { saveInFlightRef.current = false; setSaving(false); }
    };

    // The Profile screen's save sequence (utils/runningSaveRules.js), on the goal + the training set from Step 3:
    // required fields -> km x days (block) -> the plan engine's answer (block, no "שמור בכל זאת") -> the goal-time
    // guardrail ("continue anyway" unless the validator itself hard-blocks) — last, so confirming it skips nothing.
    const handleNext = async () => {
      if (saveInFlightRef.current) return;
      const d = { ...form, goal_distance: dist, goal_time: goalTime };
      const missing = runningMissingFields(d);
      if (missing.length) { dlg.showBlock(runningMissingMessage(missing)); return; }
      if (hasRace && !date) { dlg.showBlock('תאריך המרוץ הוא שדה חובה'); return; }
      if (hasRace && !raceName.trim()) { dlg.showBlock('שם מרוץ הוא שדה חובה'); return; }

      const matrix = runningMatrixCheck(d);
      if (!matrix.ok) {
        if (matrix.errorCode) { dlg.showBlock(`${matrix.reason}`); return; }
        setLoadBalance({ open: true, weeklyKm: parseFloat(d.avg_weekly_km),
                         trainingDays: runningDaysCount(d.preferred_days), requiredDays: matrix.requiredDays });
        return;
      }

      // The plan engine's own answer, before anything is saved (one request at a time, like the save itself).
      saveInFlightRef.current = true;
      setSaving(true);
      let plan;
      try {
        plan = await runningPlanCheck(d, { fetcher: authFetch, apiBase: API_BASE_URL });
      } finally { saveInFlightRef.current = false; setSaving(false); }
      if (plan.block) { dlg.showBlock(plan.block); return; }

      const result = runningGoalGuardrail(d);
      if (result.status !== 'ok') {
        setGuardrail({ result });
        return;
      }

      await persistGoal(false);
    };

    return (
      <div>
        <h2 className="text-2xl font-bold text-text mb-1">מרוץ יעד ריצה</h2>
        <p className="text-text-muted text-sm mb-5">לתכנית ממוקדת ומדויקת</p>

        <div className="space-y-4">
          {/* Distance — always visible */}
          <div>
            <label className="block text-text-body text-sm mb-2">מרחק מועדף *</label>
            <div className="flex gap-2">
              {DISTANCES.map(d => (
                <button key={d.value} type="button" onClick={() => setDist(d.value)}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all border ${
                    dist === d.value ? 'bg-pressed border-accent text-text' : 'bg-pressed border-border text-text-muted hover:border-border'
                  }`}
                >{d.label}</button>
              ))}
            </div>
            {planHint && planHint.supported === false && (
              <p className="text-quality text-xs mt-2 whitespace-pre-line" data-testid="onb-plan-hint">{planHint.userMessage}</p>
            )}
            {planHint && planHint.supported !== false && allowedRangeText(planHint, daysChosen) && (
              <p className="text-text-muted text-xs mt-2" data-testid="onb-plan-hint">
                {allowedRangeText(planHint, daysChosen)} · הנפח שלך: {form.avg_weekly_km} ק״מ
              </p>
            )}
          </div>

          {/* Goal time — always visible */}
          <div>
            <label className="block text-text-body text-sm mb-1">זמן יעד *</label>
            <button type="button" onClick={() => setLocalPicker(true)}
              className="w-full flex items-center justify-between p-3 rounded-xl bg-pressed border border-border hover:border-border transition-colors"
            >
              <span className="text-text-muted text-sm">בחר זמן יעד</span>
              {goalTime
                ? <span className="text-text-body font-mono font-bold">{goalTime}</span>
                : <span className="text-text-muted text-sm">חובה ←</span>}
            </button>
          </div>

          {/* Checkbox: יש לי תחרות קרובה */}
          <button type="button" onClick={() => setHasRace(!hasRace)}
            className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all ${
              hasRace ? 'bg-pressed border-accent' : 'bg-pressed border-border hover:border-border'
            }`}
          >
            <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
              hasRace ? 'bg-accent' : 'bg-pressed border border-border'
            }`}>
              {hasRace && <span className="text-on-accent text-sm font-bold">✓</span>}
            </div>
            <span className="text-text text-sm font-medium">יש לי תחרות קרובה</span>
          </button>

          {/* Race fields — only when checkbox is checked */}
          {hasRace && (
            <div className="space-y-3 p-3 rounded-xl bg-pressed border border-border">
              <div>
                <label className="block text-text-body text-sm mb-1">תאריך המרוץ *</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full p-3 rounded-xl bg-pressed border border-border text-text focus:outline-none focus:border-accent"
                />
              </div>
              <div>
                <label className="block text-text-body text-sm mb-1">שם המרוץ *</label>
                <input type="text" value={raceName} onChange={e => setRaceName(e.target.value)}
                  placeholder="לדוגמה: מרתון תל אביב"
                  className="w-full p-3 rounded-xl bg-pressed border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
                />
              </div>
            </div>
          )}
        </div>

        <NavButtons
          onNext={handleNext}
          nextDisabled={!dist || !goalTime || (hasRace && (!date || !raceName.trim()))}
          nextLoading={saving}
        />

        {localPicker && (
          <TimePicker
            label={`זמן יעד${dist ? ` — ${DISTANCES.find(d => d.value === dist)?.label || ''}` : ''}`}
            value={goalTime}
            showHours={dist !== '5'}
            distanceKm={distKm}
            onChange={v => setGoalTime(v)}
            onClose={() => setLocalPicker(false)}
          />
        )}

        {guardrail && (
          <GoalGuardrailModal
            result={guardrail.result}
            onAdoptSuggestion={() => {
              const suggestedLabel = guardrail.result.suggestedDistance;
              const v = distanceLabelToValue(suggestedLabel);
              if (v) {
                setDist(v);
                // Clear the goal time for the new distance; the AI will ask later.
                setTimesMap(m => ({ ...m, [v]: '' }));
              }
              setGuardrail(null);
            }}
            onContinueAnyway={async () => {
              setGuardrail(null);
              await persistGoal(true);
            }}
            onClose={() => setGuardrail(null)}
          />
        )}

        {/* km x days does not fit this goal: fixed where they are chosen (Step 3) — no save, no override. */}
        <LoadBalanceModal
          open={loadBalance.open}
          weeklyKm={loadBalance.weeklyKm}
          trainingDays={loadBalance.trainingDays}
          requiredDays={loadBalance.requiredDays}
          onFix={() => { setLoadBalance({ open: false }); setCurrentStep(3); }}
        />
        {dlg.element}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // STEP 5 — נתונים אופציונליים
  // ──────────────────────────────────────────────────────────────────
  const Step5 = () => {
    const [pr5k, setPr5k]   = useState(form.pr_5k);
    const [pr10k, setPr10k] = useState(form.pr_10k);
    const [prHalf, setPrHalf] = useState(form.pr_half);
    const [prFull, setPrFull] = useState(form.pr_full);
    const [maxHr, setMaxHr] = useState(form.max_hr);
    const [injuries, setInjuries] = useState(form.injury_prone_areas);
    const [longDay, setLongDay] = useState(form.preferred_long_run_day);
    const [localPicker, setLocalPicker] = useState(null);
    const [saving, setSaving] = useState(false);
    const dlg = useSaveDialog();

    const PRs = [
      { key: 'pr_5k',   label: '5K',     val: pr5k,   setVal: setPr5k,   km: 5,    hours: false },
      { key: 'pr_10k',  label: '10K',    val: pr10k,  setVal: setPr10k,  km: 10,   hours: true  },
      { key: 'pr_half', label: 'חצי',    val: prHalf, setVal: setPrHalf, km: 21.1, hours: true  },
      { key: 'pr_full', label: 'מרתון',  val: prFull, setVal: setPrFull, km: 42.2, hours: true  },
    ];

    const handleSave = async () => {
      if (saveInFlightRef.current) return;
      saveInFlightRef.current = true;
      setSaving(true);
      try {
        const today = new Date().toISOString().split('T')[0];
        const payload = {
          max_hr: maxHr ? parseInt(maxHr) : null,
          injury_prone_areas: injuries || null,
          preferred_long_run_day: longDay || null,
        };
        const r = await saveProfileRequest(authFetch, `${API_BASE_URL}/profile?user_id=${userId}`, payload);
        if (!r.ok) { dlg.showBlock(r.message, 'השמירה נכשלה'); return; }   // stay here — nothing was stored

        // Save PRs to race_results
        const prMap = { pr_5k: { cat: '5k', val: pr5k }, pr_10k: { cat: '10k', val: pr10k }, pr_half: { cat: 'half', val: prHalf }, pr_full: { cat: 'marathon', val: prFull } };
        for (const [, { cat, val }] of Object.entries(prMap)) {
          if (val && val !== '00:00' && val !== '0:00:00') {
            await authFetch(`${API_BASE_URL}/race-results?user_id=${userId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ distance_category: cat, race_name: 'הזנה ידנית', race_date: today, finish_time_display: val, notes: 'הוזן בעת ההרשמה' })
            }).catch(() => {});
          }
        }

        set('pr_5k', pr5k); set('pr_10k', pr10k); set('pr_half', prHalf); set('pr_full', prFull);
        set('max_hr', maxHr); set('injury_prone_areas', injuries);
        set('preferred_long_run_day', longDay);
        nextStep();
      } finally { saveInFlightRef.current = false; setSaving(false); }
    };

    return (
      <div>
        <h2 className="text-2xl font-bold text-text mb-1">פרטים נוספים</h2>
        <p className="text-text-muted text-sm mb-5">אופציונלי — ניתן לדלג</p>

        {/* PRs */}
        <div className="p-4 rounded-xl bg-pressed border border-border mb-4">
          <p className="text-text text-sm font-medium mb-1">תוצאות אחרונות</p>
          <p className="text-text-muted text-xs mb-3">ה-AI יחשב קצבי אימון מדויקים לפי תוצאותיך</p>
          <div className="divide-y divide-border">
            {PRs.map(pr => (
              <div key={pr.key} className="flex items-center justify-between py-2.5">
                <span className="text-text-body text-sm font-medium">{pr.label}</span>
                <div className="flex items-center gap-2">
                  {pr.val && <span className="text-text text-sm font-semibold tabular-nums">{pr.val}</span>}
                  <button
                    type="button"
                    onClick={() => setLocalPicker({ key: pr.key, setVal: pr.setVal, label: pr.label, km: pr.km, hours: pr.hours, val: pr.val })}
                    className="px-3 py-1 rounded-lg text-xs font-medium"
                    style={{ border: '1px solid var(--g-line)', color: pr.val ? 'var(--g-text)' : 'var(--g-sub)' }}
                  >
                    {pr.val ? 'ערוך' : 'הזן'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Max HR */}
        <div className="mb-4">
          <label className="block text-text-body text-sm mb-1">דופק מקסימלי (Max HR)</label>
          <input type="number" value={maxHr} onChange={e => setMaxHr(e.target.value)} placeholder="185"
            className="w-full p-3 rounded-xl bg-pressed border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
          />
        </div>

        {/* Injuries */}
        <div className="mb-4">
          <label className="block text-text-body text-sm mb-1">אזורים רגישים / פציעות</label>
          <textarea value={injuries} onChange={e => setInjuries(e.target.value)}
            placeholder="למשל: ברך ימין, גיד אכילס..." rows={2}
            className="w-full p-3 rounded-xl bg-pressed border border-border text-text placeholder-text-muted resize-none focus:outline-none focus:border-accent"
          />
        </div>

        <NavButtons onNext={handleSave} nextLabel="שמור והמשך ←" nextLoading={saving} />
        <button type="button" onClick={nextStep} className="w-full mt-2 py-2 text-text-muted text-sm hover:text-text-muted transition-colors">
          דלג על שלב זה →
        </button>

        {localPicker && (
          <TimePicker
            label={`שיא ל${localPicker.label}`}
            value={localPicker.val}
            showHours={localPicker.hours}
            distanceKm={localPicker.km}
            onChange={v => localPicker.setVal(v)}
            onClose={() => setLocalPicker(null)}
          />
        )}
        {dlg.element}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // STEP 6 — מתנה: תכנית שבועית
  // ──────────────────────────────────────────────────────────────────
  // STEP 6 — סיום הרשמה + יצירת תכנית ברקע
  // ──────────────────────────────────────────────────────────────────
  const Step6 = () => {
    // Plan generation phases: 'idle' → 'generating' → 'done' | 'failed'
    const [planPhase, setPlanPhase] = useState('idle');
    // Coarse progress 0-100 for the visible bar (fake-but-paced; the
    // real progress jumps to 100 only when the backend confirms).
    const [progress, setProgress] = useState(0);
    // Attempt number (0 = initial, 1+ = manual retry).
    const [attemptKey, setAttemptKey] = useState(0);

    // 2026-06-10: re-enable real plan generation in onboarding (user request).
    // History: this was reverted in b65ee66 (2026-06-07) because the regen
    // produced 31 km on a 40 km/week profile — root cause was profile-save
    // timing (DB commit not visible to the regen worker yet). We now:
    //   1. Wait 3 sec to ensure profile is fully committed.
    //   2. Call regenerate-all explicitly (current + next week, all sports).
    //   3. If anything fails, fall back to the old behaviour (mark done,
    //      let WeeklyPlan's bgSync take over).
    // Backend now also has a hard min-km validator and richer profile reads,
    // so the original timing issue should no longer cause wrong volumes.
    useEffect(() => {
      let cancelled = false;
      let tickHandle = null;

      // Fire plan generation ONCE per user. Step6 is an inline component, so ANY
      // OnboardingWizard re-render gives it a new function identity → React remounts
      // it → this effect re-runs. Without this guard that became an infinite
      // welcome-token / regenerate-all loop (regenerate-all is a background job that
      // runs server-side regardless, so showing 'done' here is safe). Manual retry
      // (attemptKey>0) bypasses the guard.
      const _genKey = `onb_plangen_${userId}`;
      if (attemptKey === 0) {
        let _already = false;
        try { _already = !!sessionStorage.getItem(_genKey); } catch {}
        if (_already) {
          setPlanPhase('done');
          setProgress(100);
          return;
        }
        try { sessionStorage.setItem(_genKey, '1'); } catch {}
      }

      setPlanPhase('idle');
      setProgress(0);

      // Coarse animated progress: 0 → 90 over ~30s. Real completion bumps to 100.
      const tick = () => {
        if (cancelled) return;
        setProgress(p => (p < 90 ? p + 1 : p));
        tickHandle = setTimeout(tick, 350);
      };
      tickHandle = setTimeout(tick, 350);

      (async () => {
        try {
          // 1. Grant welcome token (best-effort) — only on the first attempt.
          if (attemptKey === 0) {
            try {
              let recaptchaToken = null;
              const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
              if (siteKey && window.grecaptcha) {
                try { recaptchaToken = await window.grecaptcha.execute(siteKey, { action: 'register' }); } catch {}
              }
              await authFetch(`${API_BASE_URL}/onboarding/welcome-token`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: userId, recaptcha_token: recaptchaToken })
              });
            } catch {}
          }

          if (cancelled) return;
          // Before the first plan (owner 2026-11-02): the running goal the SERVER holds — never the wizard's memory.
          // Missing -> no plan request, no 'completed' mark; the athlete goes back to the goal step.
          if (!form.sports.length || form.sports.includes('running')) {
            let _saved = null;
            try {
              const pr = await authFetch(`${API_BASE_URL}/profile?user_id=${userId}`);
              _saved = pr && pr.ok ? await pr.json() : null;
            } catch { _saved = null; }
            if (cancelled) return;
            if (!_saved) {                                       // could not verify: retry, never generate blind
              try { sessionStorage.removeItem(_genKey); } catch {}
              setPlanPhase('failed');
              return;
            }
            if (!runningGoalSaved(_saved)) {
              try { sessionStorage.removeItem(_genKey); } catch {}
              setPlanPhase('missing');
              return;
            }
          }
          setPlanPhase('generating');

          // 2. Wait for profile DB commit to land (avoid the b65ee66 issue
          // where regen saw a partial profile and produced wrong volume).
          await new Promise(r => setTimeout(r, 3000));
          if (cancelled) return;

          // 3. Trigger real plan generation — current + next week, all
          // active sports the user picked in step 1 (defaults to running).
          const _sportsActive = (form.sports_active || 'running')
            .split(',').map(s => s.trim()).filter(Boolean);
          const _targetSports = _sportsActive.length ? _sportsActive : ['running'];

          let regenOk = false;
          try {
            const result = await planRegenRequest({
              url: `${API_BASE_URL}/weekly-plan/regenerate-all?user_id=${userId}`,
              body: {
                sports: _targetSports,
                weeks: ['current', 'next', 'week_plus2', 'week_plus3'],
                triggered_by_user: true,
              },
              pollUserId: userId,
              changedFields: ['Onboarding'],
            });
            regenOk = !!(result && result.success);
            if (!regenOk) {
              console.warn('Onboarding regen returned not-success:', result?.error);
            }
          } catch (regenErr) {
            console.warn('Onboarding regen threw, falling back to bgSync:', regenErr);
          }

          if (cancelled) return;
          setProgress(100);
          // "התוכנית שלך מוכנה!" only when the plan was really built (owner 2026-11-03). A failed or unreachable
          // build shows the existing retry screen ("רגע, ננסה שוב" / "נסה שוב") instead of claiming success.
          setPlanPhase(regenOk ? 'done' : 'failed');
        } catch (e) {
          if (cancelled) return;
          setPlanPhase('failed');
          console.error('Onboarding plan generation threw:', e);
        } finally {
          if (tickHandle) clearTimeout(tickHandle);
        }
      })();

      return () => {
        cancelled = true;
        if (tickHandle) clearTimeout(tickHandle);
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attemptKey]);

    const handleFinish = () => {
      localStorage.setItem(`onboarding_${userId}`, 'completed');
      try {
        sessionStorage.removeItem(`onb_step_${userId}`);      // done → next onboarding starts at 1
        sessionStorage.removeItem(`onb_plangen_${userId}`);   // …and re-generates its plan
      } catch {}
      if (onComplete) onComplete();
    };

    const planReady = planPhase === 'done';
    const planFailed = planPhase === 'failed';

    if (planPhase === 'missing') {
      return (
        <div className="text-center" data-testid="onb-goal-missing">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed text-accent flex items-center justify-center mb-4">
            <ArrowsClockwise size={30} weight="bold" />
          </div>
          <h2 className="text-2xl font-bold text-text mb-2">חסר יעד בפרופיל</h2>
          <p className="text-text-body text-sm mb-5">{GOAL_MISSING_MSG}</p>
          <button type="button" onClick={() => setCurrentStep(4)} className="w-full h-12 rounded-full btn-primary">
            חזרה לצעד היעד
          </button>
        </div>
      );
    }

    return (
      <div className="text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-pressed text-accent flex items-center justify-center mb-4">
          {planFailed ? <ArrowsClockwise size={30} weight="bold" /> : <CheckCircle size={30} weight="bold" />}
        </div>
        <h2 className="text-2xl font-bold text-text mb-2">
          {planFailed ? 'רגע, ננסה שוב' : planReady ? 'התוכנית שלך מוכנה!' : 'ברוך הבא!'}
        </h2>
        <p className="text-text-body text-sm mb-1">
          {planFailed
            ? 'אנחנו בונים לך את התוכנית — לפעמים זה לוקח עוד רגע.'
            : planReady
            ? 'בנינו לך הכל מהפרופיל — קדימה נתחיל'
            : 'בונה עבורך תוכנית אימונים מותאמת אישית...'}
        </p>
        {!planReady && (
          <p className="text-xs text-text-body mb-3">
            {planFailed ? 'נסה שוב — או דלג והשלם את התוכנית מהמסך הראשי.' : 'מכין תוכנית...'}
          </p>
        )}

        {/* Real progress bar that reflects backend state. */}
        {!planFailed && (
          <div className="w-full h-2 rounded-full bg-pressed overflow-hidden mb-5">
            <div
              className="h-full transition-all duration-700"
              style={{
                width: `${progress}%`,
                background: 'var(--g-accent)',
              }}
            />
          </div>
        )}

        {planFailed && (
          <button
            type="button"
            onClick={() => setAttemptKey(k => k + 1)}
            className="w-full mb-3 h-12 rounded-full btn-primary"
          >
            נסה שוב
          </button>
        )}

        {/* Completion actions — only once the plan is actually ready. During
            generation the progress bar above is the whole UI (unchanged). The plan was already
            built from the profile in this step; Garmin is connected later from Profile. */}
        {planReady && (
          <div className="space-y-3 text-right" dir="rtl">
            {/* Plan hero — proof of value already delivered */}
            <div className="p-4 rounded-[16px] bg-surface border border-border">
              <p className="text-text font-bold text-base mb-0.5">בנינו לך תוכנית אימונים ל-4 שבועות</p>
              <p className="text-text-body text-xs mb-3">היא מחכה לך בדף התוכניות</p>
              <button type="button" onClick={onViewPlan}
                className="w-full h-12 rounded-full btn-primary transition-all">
                צפה בתוכנית שלי
              </button>
            </div>

            {/* Garmin is connected through Stridee from Profile → חיבורי אימונים after onboarding
                (Strava removal C2, 2026-10-21: no Strava prompt). */}
            {(
              <div className="p-4 rounded-[16px] bg-surface border border-border" data-testid="onb-garmin-next">
                <p className="text-text font-bold text-base mb-0.5 flex items-center gap-2">
                  <GarminLogo size={28} decorative /> חיבור Garmin
                </p>
                <p className="text-text-body text-xs mb-3 leading-relaxed">
                  את Garmin מחברים מהפרופיל ← חיבורי אימונים (דרך Stridee). אחרי החיבור המאמן ינתח את האימונים שלך.
                </p>
                <button type="button" onClick={handleFinish}
                  className="w-full h-12 rounded-full font-bold border border-border text-text-body">
                  בוא נתחיל
                </button>
              </div>
            )}

            {/* Pilot perk — kept (positive value-prop), moved below, print trimmed */}
            <div className="p-4 rounded-xl bg-pressed border border-border">
              <p style={{ color: 'var(--g-accent)', fontSize: 14, fontWeight: 600, lineHeight: 1.5 }}>
                כמשתמש פיילוט נבחר — השימוש חינמי לצמיתות!
              </p>
              <p style={{ color: 'var(--g-sub)', fontSize: 12, marginTop: 8 }}>נשמח לקבל פידבק שיעזור לנו להשתפר</p>
            </div>

            <p className="text-center text-text-muted text-xs pt-1">10 שיחות ללא עלות ביום</p>
          </div>
        )}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // STEP 7 — פרופיל רכיבה (Phase 1: כביש בלבד, שדות מצומצמים)
  // ──────────────────────────────────────────────────────────────────
  const Step7 = () => {
    const [level, setLevel] = useState(form.cycling_level);
    const [days, setDays] = useState(
      form.preferred_cycling_days ? form.preferred_cycling_days.split(' ').filter(Boolean) : []
    );
    const [hours, setHours] = useState(form.weekly_cycling_hours || '');
    const [longestRide, setLongestRide] = useState(form.recent_longest_ride_km || '');
    const [maxHr, setMaxHr] = useState(form.max_hr || '');
    const [saving, setSaving] = useState(false);

    // Phase 1: road only — discipline hardcoded, no bike-type picker.
    // 3 levels (no professional tier for now).
    const CYCLING_LEVELS = [
      { key: 'beginner',     label: 'מתחיל',  icon: '🌱', sub: 'עד 5 ש׳/שבוע' },
      { key: 'intermediate', label: 'בינוני',  icon: '💪', sub: '6–9 ש׳/שבוע' },
      { key: 'advanced',     label: 'מתקדם',  icon: '🔥', sub: '9+ ש׳/שבוע' },
    ];

    const toggleDay = (d) => {
      const order = DAYS.map(x => x.key);
      setDays(prev => {
        const next = prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d];
        return next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
      });
    };

    const handleNext = async () => {
      if (!level) { alert('בחר רמת רוכב'); return; }
      if (!days.length) { alert('בחר לפחות יום רכיבה אחד'); return; }
      if (!hours || parseFloat(hours) <= 0) { alert('הזן שעות רכיבה שבועיות'); return; }
      setSaving(true);
      try {
        const payload = {
          // Phase 1: road discipline hardcoded — no picker shown.
          discipline: 'road',
          bike_types: 'road',
          cycling_level: level,
          weekly_cycling_hours: parseFloat(hours),
          preferred_cycling_days: days.join(' '),
        };
        // Optional fields — skip when blank so we don't overwrite
        // existing data with empty values.
        if (longestRide && parseFloat(longestRide) > 0)
          payload.recent_longest_ride_km = parseFloat(longestRide);
        if (maxHr && parseInt(maxHr) > 0)
          payload.max_hr = parseInt(maxHr);

        const resp = await authFetch(`${API_BASE_URL}/profile/cycling?user_id=${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!resp.ok) {
          let humanMsg = '';
          try {
            const _body = await resp.json();
            if (_body?.detail?.message) humanMsg = _body.detail.message;
            else if (typeof _body?.detail === 'string') humanMsg = _body.detail;
            else if (_body?.message) humanMsg = _body.message;
          } catch {
            try { humanMsg = (await resp.text()).slice(0, 200); } catch {}
          }
          console.error(`PUT /profile/cycling failed: ${resp.status}`, humanMsg);
          alert(humanMsg || `שגיאה בשמירה (${resp.status})`);
          return;
        }
        set('cycling_level', level);
        set('bike_types', ['road']);
        set('weekly_cycling_hours', hours);
        set('preferred_cycling_days', days.join(' '));
        if (longestRide) set('recent_longest_ride_km', longestRide);
        if (maxHr) set('max_hr', maxHr);
        nextStep();
      } catch (err) {
        console.error('PUT /profile/cycling threw:', err);
        alert('שגיאה בשמירה — בדוק חיבור לאינטרנט ונסה שוב');
      }
      finally { setSaving(false); }
    };

    const nextDisabled = !level || !days.length || !hours || parseFloat(hours) <= 0;

    return (
      <div>
        <h2 className="text-2xl font-bold text-text mb-1">פרופיל רכיבה</h2>
        <p className="text-text-muted text-sm mb-5">רכיבת כביש · הבסיס לחישוב תכנית האימון</p>

        {/* Level — 3 options with hour-range subtitle */}
        <div className="mb-4">
          <label className="block text-text-body text-sm mb-2">רמת רוכב *</label>
          <div className="flex gap-2">
            {CYCLING_LEVELS.map(l => (
              <button key={l.key} type="button" onClick={() => setLevel(l.key)}
                className={`flex-1 py-2.5 rounded-xl font-medium transition-all border ${
                  level === l.key ? 'bg-pressed border-accent text-text' : 'bg-pressed border-border text-text-muted hover:border-border'
                }`}
              >
                <div className="text-lg mb-0.5">{l.icon}</div>
                <div className="text-xs font-bold">{l.label}</div>
                <div className="text-[10px] text-text-muted mt-0.5">{l.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Preferred days */}
        <div className="mb-4">
          <label className="block text-text-body text-sm mb-2">ימי רכיבה מועדפים *</label>
          <div className="flex gap-1.5 justify-between">
            {DAYS.map(d => (
              <button key={d.key} type="button" onClick={() => toggleDay(d.key)}
                className={`w-10 h-10 rounded-lg font-bold text-sm transition-all ${
                  days.includes(d.key) ? 'bg-accent text-on-accent' : 'bg-pressed text-text-muted hover:bg-pressed'
                }`}
              >{d.label}</button>
            ))}
          </div>
          {days.length > 0 && (
            <p className="text-text-body text-xs mt-1.5 text-center">{days.length} ימי רכיבה בשבוע</p>
          )}
        </div>

        {/* Weekly hours — MANDATORY */}
        <div className="mb-4">
          <label className="block text-text-body text-sm mb-2">שעות רכיבה שבועיות *</label>
          <input type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)}
            placeholder="לדוגמה: 6"
            className="w-full p-3 rounded-xl bg-pressed border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
          />
          <p className="text-text-muted text-xs mt-1">המדד המוביל לבניית נפח האימון השבועי</p>
        </div>

        {/* Recent longest ride — OPTIONAL */}
        <div className="mb-4">
          <label className="block text-text-body text-sm mb-2">הרכיבה הארוכה ביותר שלך לאחרונה (ק"מ)</label>
          <input type="number" step="1" value={longestRide} onChange={e => setLongestRide(e.target.value)}
            placeholder="לדוגמה: 80"
            className="w-full p-3 rounded-xl bg-pressed border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
          />
          <p className="text-text-muted text-xs mt-1">מונע הצעת ארוכה מסוכנת בשבוע הראשון</p>
        </div>

        {/* Max HR — OPTIONAL, fallback = 220-age */}
        <div className="mb-4">
          <label className="block text-text-body text-sm mb-2">דופק מקסימלי Max HR (לא חובה)</label>
          <input type="number" value={maxHr} onChange={e => setMaxHr(e.target.value)}
            placeholder={form.age ? String(220 - parseInt(form.age || 30)) : '190'}
            min="120" max="220"
            className="w-full p-3 rounded-xl bg-pressed border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
          />
          <p className="text-text-muted text-xs mt-1">ריק — ישתמש בנוסחת 220 פחות גיל</p>
        </div>

        <NavButtons onNext={handleNext} nextDisabled={nextDisabled} nextLoading={saving} />
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────
  // STEP 8 — מרוץ יעד רכיבה (Phase 1: 3 מרחקים, checkbox הפוך)
  // ──────────────────────────────────────────────────────────────────
  const Step8 = () => {
    // noRace=true means "training without a goal race" — checkbox checked = no race.
    // Default: has_cycling_race===false → noRace=true; anything else → noRace=false (show fields).
    const [noRace, setNoRace] = useState(form.has_cycling_race === false);
    const [dist, setDist] = useState(form.cycling_goal_distance);
    const [date, setDate] = useState(form.cycling_goal_date);
    const [raceName, setRaceName] = useState(form.cycling_race_name);
    const [goalTime, setGoalTime] = useState(form.cycling_goal_time);
    const [localPicker, setLocalPicker] = useState(false);
    const [saving, setSaving] = useState(false);

    // Phase 1: road cycling distances — 60 / 100 / 160 km.
    const CYCLING_DISTANCES = ['60', '100', '160'];

    const handleNext = async () => {
      if (!noRace && !dist) { alert('בחר מרחק תחרות'); return; }
      if (!noRace && !raceName.trim()) { alert('הזן שם תחרות'); return; }
      if (!noRace && !date) { alert('בחר תאריך תחרות'); return; }
      setSaving(true);
      try {
        const payload = {
          has_race: !noRace,
          cycling_goal_event: !noRace && raceName.trim() ? raceName.trim() : null,
          cycling_goal_date: !noRace ? date : null,
          cycling_goal_distance: !noRace ? dist : null,
          cycling_goal_time: !noRace && goalTime ? goalTime : null,
        };
        await authFetch(`${API_BASE_URL}/profile/cycling?user_id=${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        set('has_cycling_race', !noRace);
        set('cycling_goal_distance', !noRace ? dist : '');
        set('cycling_goal_date', !noRace ? date : '');
        set('cycling_race_name', raceName);
        set('cycling_goal_time', goalTime);
        nextStep();
      } catch { alert('שגיאה בשמירה'); }
      finally { setSaving(false); }
    };

    const DistBtn = ({ value, onSel, selected }) => (
      <button type="button" onClick={() => onSel(value)}
        className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-all border ${
          selected === value ? 'bg-pressed border-accent text-text' : 'bg-pressed border-border text-text-muted hover:border-border'
        }`}
      >{value} ק"מ</button>
    );

    return (
      <div>
        <h2 className="text-2xl font-bold text-text mb-1">מרוץ יעד רכיבה</h2>
        <p className="text-text-muted text-sm mb-5">לתכנית ממוקדת ומדויקת</p>

        {/* "no race" checkbox — checked = no goal race */}
        <button type="button" onClick={() => setNoRace(!noRace)}
          className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all mb-4 ${
            noRace ? 'bg-pressed border-border' : 'bg-pressed border-border hover:border-border'
          }`}
        >
          <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all ${
            noRace ? 'bg-accent' : 'bg-pressed border border-border'
          }`}>
            {noRace && <span className="text-on-accent text-sm font-bold">✓</span>}
          </div>
          <span className="text-text text-sm font-medium">מתאמן ללא תחרות — אין לי תחרות יעד</span>
        </button>

        {!noRace && (
          <div className="space-y-3">
            {/* Distance */}
            <div>
              <label className="block text-text-body text-sm mb-2">מרחק תחרות *</label>
              <div className="flex gap-2">
                {CYCLING_DISTANCES.map(d => <DistBtn key={d} value={d} onSel={setDist} selected={dist} />)}
              </div>
            </div>
            {/* Date */}
            <div>
              <label className="block text-text-body text-sm mb-1">תאריך *</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full p-3 rounded-xl bg-pressed border border-border text-text focus:outline-none focus:border-accent"
              />
            </div>
            {/* Goal time (optional) */}
            <div>
              <label className="block text-text-body text-sm mb-1">זמן יעד (לא חובה)</label>
              <button type="button" onClick={() => setLocalPicker(true)}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-pressed border border-border hover:border-border transition-colors"
              >
                <span className="text-text-muted text-sm">בחר זמן יעד</span>
                {goalTime
                  ? <span className="text-text-body font-mono font-bold">{goalTime}</span>
                  : <span className="text-text-muted text-sm">אופציונלי</span>}
              </button>
            </div>
            {/* Race name (optional) */}
            <div>
              <label className="block text-text-body text-sm mb-1">שם התחרות *</label>
              <input type="text" value={raceName} onChange={e => setRaceName(e.target.value)}
                placeholder="לדוגמה: גראן פונדו תל אביב"
                className="w-full p-3 rounded-xl bg-pressed border border-border text-text placeholder-text-muted focus:outline-none focus:border-accent"
              />
            </div>
          </div>
        )}

        <NavButtons
          onNext={handleNext}
          nextDisabled={!noRace && (!dist || !raceName.trim() || !date)}
          nextLoading={saving}
        />

        {localPicker && (
          <TimePicker
            label="זמן יעד רכיבה"
            value={goalTime}
            showHours={true}
            onChange={v => setGoalTime(v)}
            onClose={() => setLocalPicker(false)}
          />
        )}
      </div>
    );
  };

  // ── Routing ─────────────────────────────────────────────────────
  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1 />;
      case 2: return <Step2 />;
      case 3: return <Step3 />;
      case 4: return <Step4 />;
      case 5: return <Step5 />;
      case 6: return <Step6 />;
      case 7: return <Step7 />;
      case 8: return <Step8 />;
      // Step 9 is a top-level component (stable identity): no remount loop, nothing runs on mount
      case 9: return <NotificationOptInStep userId={userId} apiBase={API_BASE_URL} onContinue={nextStep} />;
      default: return <Step1 />;
    }
  };

  const STEP_LABEL_MAP = {
    1: 'פרטים אישיים',
    2: 'ענף ספורט',
    3: 'פרופיל ריצה',
    7: 'פרופיל רכיבה',
    4: 'מרוץ יעד ריצה',
    8: 'מרוץ יעד רכיבה',
    5: 'נתונים נוספים',
    9: 'התראות',
    6: 'מתנה',
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-bg" dir="rtl">

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center py-8 px-4">
        <div className="w-full max-w-md">

          {/* Logo */}
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-text">
              Ritmo<span className="text-accent">IQ</span>
            </h1>
            <p className="text-text-muted text-xs mt-0.5">{STEP_LABEL_MAP[currentStep]}</p>
          </div>

          {/* Progress */}
          <ProgressBar />

          {/* Card */}
          <div className="bg-surface rounded-[16px] p-5 border border-border">
            {renderStep()}
          </div>

          {/* Step counter */}
          <p className="text-center text-text-muted text-xs mt-3">
            שלב {getStepSequence(form.sports).indexOf(currentStep) + 1} מתוך {getStepSequence(form.sports).length}
          </p>
        </div>
      </div>

      {pickerOpen && (
        <TimePicker
          label={pickerOpen.label}
          value={pickerOpen.val}
          showHours={pickerOpen.hours}
          distanceKm={pickerOpen.km}
          onChange={v => pickerOpen.setVal(v)}
          onClose={() => setPickerOpen(null)}
        />
      )}
    </div>
  );
}

export default OnboardingWizard;
